// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Heartbeat Service
// Periodic ping to the backend. Updates online status, opening hours,
// and cash session closing state from the heartbeat response.
// ---------------------------------------------------------------------------

import { apiClient } from '@/services/api-client';
import { useAppStore } from '@/store/app-store';
import { useCashStore } from '@/store/cash-store';
import { HEARTBEAT_INTERVAL } from '@/lib/constants';

// ---------------------------------------------------------------------------
// Heartbeat Service
// ---------------------------------------------------------------------------

class HeartbeatService {
  private interval: ReturnType<typeof setInterval> | null = null;

  // ---- lifecycle ----------------------------------------------------------

  /**
   * Start the heartbeat interval. Runs immediately, then every
   * HEARTBEAT_INTERVAL ms.
   */
  start(): void {
    this.stop();

    // Immediate first check
    this.check().catch(() => {});

    this.interval = setInterval(() => {
      this.check().catch(() => {});
    }, HEARTBEAT_INTERVAL);
  }

  /**
   * Stop the heartbeat interval.
   */
  stop(): void {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  // ---- check --------------------------------------------------------------

  private async check(): Promise<void> {
    try {
      const response = await apiClient.heartbeat();

      // Mark online
      useAppStore.getState().setOnline(true);

      // Route opening_hours to cash store
      if (response.opening_hours) {
        const isFerme = response.opening_hours.is_open === false;
        useCashStore.getState().setFerme(isFerme);
      }

      // Route cash_session closing state
      if (response.cash_session) {
        const closingPending =
          response.cash_session.closing_pending === true;
        useCashStore.getState().setClosingPending(closingPending);
      }
    } catch (error) {
      // Only mark offline if it is NOT an auth error
      if (
        !(error instanceof Error) ||
        !error.message.includes('Session expiree')
      ) {
        useAppStore.getState().setOnline(false);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const heartbeatService = new HeartbeatService();
